import { config as companyTableConfig } from '@aibees/elephant/feature/search-table-company/config';
import { useBrowseCompanySearchCore } from '@aibees/elephant/feature/search-table-company/hooks/use-browse-company-search';
import { config as peopleTableConfig } from '@aibees/elephant/feature/search-table-people/config';
import { useBrowsePeopleSearchCore } from '@aibees/elephant/feature/search-table-people/hooks/use-browse-people-search';
import type { PeopleTableInfo } from '@aibees/elephant/feature/search-table-people/types';
import { mapToSuggestionOption } from '@aibees/elephant/feature/suggestion-search-bar/utils';
import {
  useSearchModeFromRoute,
  useSearchQueryFilter,
  useSearchTypeFromRoute,
} from '@aibees/elephant/hooks';
import { useModalityChangeCounterStore } from '@aibees/elephant/store/modality-change-counter';
import { SearchType } from '@aibees/elephant/types';
import { toNumber } from '@aibees/shared/utils';
import { useWorkspace } from '@aibees/shared/workspace';
import { useDeferredValue, useMemo, type PropsWithChildren } from 'react';
import { SearchBrowseDataContext } from './context';
import type { SearchBrowseDataContextValue } from './types';

function mapToDefaultValues(
  type: SearchType,
  companyData: SearchBrowseDataContextValue['companyData'],
  peopleData: SearchBrowseDataContextValue['peopleData']
): SearchBrowseDataContextValue['defaultValues'] {
  if (type === SearchType.Company) {
    return (
      companyData?.top
        ?.map((item) =>
          mapToSuggestionOption(
            item,
            'id',
            'summary.name',
            'link.linkedin',
            'link.linkedin',
            'summary.logo.source'
          )
        )
        .filter(Boolean) ?? []
    );
  }
  return (
    peopleData?.top
      ?.map((item) =>
        mapToSuggestionOption(
          item,
          'id',
          'profile.full_name',
          'link.linkedin',
          'link.linkedin',
          'profile.picture.source'
        )
      )
      .filter(Boolean) ?? []
  );
}

/**
 * Fetches company and people browse data once and provides it via context.
 * Used by useDefaultValues and useLookalikeHandler so they do not call
 * useBrowseCompanySearchCore/useBrowsePeopleSearchCore per table row.
 * Must be mounted inside SearchParamsSourceProvider.
 */
export function SearchBrowseDataProvider({ children }: PropsWithChildren) {
  const workspace = useWorkspace()?.id;
  const type = useSearchTypeFromRoute();
  const mode = useSearchModeFromRoute();
  const { params } = useSearchQueryFilter();
  const deferredParams = useDeferredValue(params);
  const { counter: modalityVersion } = useModalityChangeCounterStore();

  const companyDeps = useMemo(
    () => ({
      workspace,
      type,
      mode,
      searchParams: deferredParams,
      modalityVersion,
    }),
    [workspace, type, mode, deferredParams, modalityVersion]
  );

  const peopleDeps = useMemo(
    () => ({
      workspace,
      type,
      searchParams: deferredParams,
      modalityVersion,
    }),
    [workspace, type, deferredParams, modalityVersion]
  );

  const companyRequest = useMemo(
    () => ({
      page: toNumber(params?.page) ?? 0,
      size: toNumber(params?.size) ?? 25,
      sort:
        companyTableConfig.prependSortingFields &&
        Object.keys(companyTableConfig.prependSortingFields)
          .map(
            (x) =>
              `${x}:${companyTableConfig.prependSortingFields?.[x] as string}`
          )
          .join(','),
    }),
    [params?.page, params?.size]
  );

  const peopleRequest = useMemo(
    () => ({
      page: toNumber(params?.page) ?? 0,
      size: toNumber(params?.size) ?? 25,
      sort:
        peopleTableConfig.prependSortingFields &&
        Object.keys(peopleTableConfig.prependSortingFields)
          .map(
            (x) =>
              `${x}:${peopleTableConfig.prependSortingFields?.[x] as string}`
          )
          .join(','),
    }),
    [params?.page, params?.size]
  );

  const { data: companyData } = useBrowseCompanySearchCore(
    companyRequest,
    companyDeps,
    { enabled: type === SearchType.Company },
    true
  );

  const { data: peopleData } = useBrowsePeopleSearchCore<PeopleTableInfo>(
    peopleRequest,
    peopleDeps,
    { enabled: type === SearchType.People },
    true
  );

  const searchType = type ?? SearchType.Company;

  const defaultValues = useMemo(
    () => mapToDefaultValues(searchType, companyData, peopleData),
    [searchType, companyData, peopleData]
  );

  const top = useMemo(
    () =>
      (searchType === SearchType.Company
        ? companyData?.top?.length
        : peopleData?.top?.length) ?? 0,
    [searchType, companyData, peopleData]
  );

  const value = useMemo<SearchBrowseDataContextValue>(
    () => ({
      companyData,
      peopleData,
      defaultValues,
      top,
    }),
    [companyData, peopleData, defaultValues, top]
  );

  return (
    <SearchBrowseDataContext.Provider value={value}>
      {children}
    </SearchBrowseDataContext.Provider>
  );
}
